import { Product } from "@~/types/types.ts";
import { useState, type ComponentProps } from "react";
import styles from "@~/product-card-components/QuickView/QuickView.module.scss";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { VisibleOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/VisibleOutlined";
import { QuickViewModal } from "@~/product-card-components/__shared__/QuickViewModal/QuickViewModal.tsx";
import { useFeatureFlags } from "@~/flags/flags";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

type VisibleOutlinedIconProps = Omit<
  ComponentProps<typeof VisibleOutlined>,
  "size"
>;

// Used to stop the IconButton from rendering the icon as "small"
const VisibleOutlinedIcon = ({
  className,
  title,
  dataTestId,
}: VisibleOutlinedIconProps) => (
  <VisibleOutlined
    className={className}
    title={title}
    dataTestId={dataTestId}
  />
);

type Props = {
  product: Product;
  handleIconClick?: () => void;
  config?: string | undefined;
  showOnHoverOnly?: boolean;
};

export function QuickViewIcon({
  product,
  handleIconClick,
  config,
  showOnHoverOnly = false,
}: Props) {
  const [showQuickView, setShowQuickView] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleClick = () => {
    if (handleIconClick) {
      handleIconClick();
    }
    setShowQuickView(true);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <>
      <IconButton
        icon={VisibleOutlinedIcon}
        aria-label={`Quick view for ${product.title}`}
        className={cx("quick-view-icon", {
          "quick-view-icon--hover-mode": showOnHoverOnly,
          "quick-view-icon--focused": showOnHoverOnly && isFocused,
        })}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        size="small"
        title={`Quick view`}
        data-testid="icon-quick-view-button"
      />
      {showQuickView && (
        <QuickViewModal
          product={product}
          showModal={true}
          onClose={() => setShowQuickView(false)}
          originator={"QUICK_VIEW_ICON"}
          config={config}
        />
      )}
    </>
  );
}
